import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, TextField, Chip, FormControlLabel, Switch, Dialog, DialogTitle, DialogContent, DialogActions,
  TablePagination, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

import { customerLogsAPI, customersAPI } from '../services/api';

const NATIVE = { select: { native: true }, inputLabel: { shrink: true } };
const SHRINK = { inputLabel: { shrink: true } };
const errText = (err, fallback) => err.response?.data?.detail || fallback;
const fmtTime = (d) => (d ? new Date(d).toLocaleString() : '-');
const ENVIRONMENTS = ['Development', 'Test', 'Staging', 'Production', 'Training'];
const MAX_ZIP = 500;
const fileKey = (f) => `${f.server}/${f.log_type}/${f.name}`;
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b >= 1024 ? `${Math.round(b / 1024)} KB` : `${b} B`);

const blobText = (blob) => (blob.text ? blob.text() : new Promise((resolve, reject) => {
  const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsText(blob);
}));

function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

function Highlighted({ text, term }) {
  if (!term) return text;
  const parts = text.split(new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return parts.map((p, i) => (p.toLowerCase() === term.toLowerCase() ? <mark key={i}>{p}</mark> : p));
}

// Entries in an SDMS (HTML) log: the time and message lines stand out, stack frames are muted, separators are faint
const htmlLineSx = (l) => {
  if (/^-{20,}$/.test(l)) return { color: 'text.disabled' };
  if (/^(Time|Message):/.test(l)) return { fontWeight: 'bold' };
  if (/^\s+at /.test(l) || /^--- End of inner exception/.test(l)) return { color: 'text.secondary' };
  return undefined;
};

function Viewer({ file, params, onClose }) {
  const [data, setData] = useState(null);
  const [tail, setTail] = useState(false);
  const [error, setError] = useState(null);
  const [term, setTerm] = useState('');
  const [onlyMatches, setOnlyMatches] = useState(false);
  const query = useMemo(() => ({ ...params, server: file.server, log_type: file.log_type, name: file.name }), [params, file]);

  useEffect(() => {
    setData(null); setError(null);
    customerLogsAPI.file({ ...query, tail }).then((r) => setData(r.data)).catch((err) => setError(errText(err, 'Could not open the log file')));
  }, [query, tail]);

  const download = async () => {
    try { const res = await customerLogsAPI.download(query); saveBlob(res.data, file.name); } catch (err) { setError(errText(err, 'Could not download the file')); }
  };
  const lines = useMemo(() => (data ? data.text.split('\n') : []), [data]);
  const shown = useMemo(() => (onlyMatches && term ? lines.filter((l) => l.toLowerCase().includes(term.toLowerCase())) : lines), [lines, onlyMatches, term]);
  const matches = term ? lines.filter((l) => l.toLowerCase().includes(term.toLowerCase())).length : 0;

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ wordBreak: 'break-all' }}>{file.name}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {file.server} / {file.log_type}{data && ` / ${fmtSize(data.size)} unzipped / synced ${fmtTime(data.synced_at)}`}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
          <TextField size="small" label="Search in this file" value={term} onChange={(e) => setTerm(e.target.value)} sx={{ minWidth: 220 }} />
          <FormControlLabel control={<Switch checked={onlyMatches} onChange={(e) => setOnlyMatches(e.target.checked)} disabled={!term} />} label="Matching lines only" />
          {term && data && <Chip size="small" label={`${matches} matching lines`} />}
          {data?.truncated && (
            <ToggleButtonGroup size="small" exclusive value={tail ? 'end' : 'start'} onChange={(_, v) => v && setTail(v === 'end')}>
              <ToggleButton value="start">Start of file</ToggleButton><ToggleButton value="end">End of file</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
        {data?.format === 'html' && <Alert severity="info" sx={{ mb: 1 }}>This log is saved as HTML{data.encoding && data.encoding.startsWith('UTF-16') ? ' in a UTF-16 format' : ''}. It is shown here as plain text so it reads properly; Download gives the original file.</Alert>}
        {data?.truncated && <Alert severity="info" sx={{ mb: 1 }}>This file is larger than 1 MB, so only its {tail ? 'end' : 'start'} is shown. Download it to read everything.</Alert>}
        {data === null && !error ? <CircularProgress size={24} /> : (
          <Box component="pre" sx={{ m: 0, p: 1.5, maxHeight: '55vh', overflow: 'auto', bgcolor: 'action.hover', borderRadius: 1, fontSize: '0.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {shown.map((l, i) => <div key={i} style={{ minHeight: '1em' }}><Box component="span" sx={data?.format === 'html' ? htmlLineSx(l) : undefined}><Highlighted text={l} term={term} /></Box></div>)}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button startIcon={<DownloadIcon />} onClick={download}>Download</Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function MissingEnvironmentDialog({ initialCustomerId, onClose, onSent }) {
  const [customers, setCustomers] = useState(null);
  const [form, setForm] = useState({ customer_id: initialCustomerId || '', environment: '', system_url: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { customersAPI.list().then((r) => setCustomers(r.data)).catch(() => { setCustomers([]); setError('Could not load the customer list'); }); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const ready = form.customer_id && form.environment && form.system_url.trim();

  const send = async () => {
    setBusy(true); setError(null);
    try {
      const res = await customerLogsAPI.missingEnvironment({ ...form, customer_id: Number(form.customer_id), note: form.note.trim() || null });
      onSent(res.data);
    } catch (err) { setError(errText(err, 'Could not send the request')); setBusy(false); }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Report a missing environment</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Can't find the customer's environment in the list? Tell the Cloud team and they will add it. They get an email, and you are copied.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField select label="Customer" value={form.customer_id} onChange={set('customer_id')} slotProps={NATIVE} required>
            <option value="">{customers === null ? 'Loading...' : 'Select a customer'}</option>
            {(customers || []).map((c) => <option key={c.id} value={c.id}>{c.cust_code} - {c.cust_name}</option>)}
          </TextField>
          <TextField select label="Environment" value={form.environment} onChange={set('environment')} slotProps={NATIVE} required>
            <option value="">Select an environment</option>
            {ENVIRONMENTS.map((e) => <option key={e} value={e}>{e}</option>)}
          </TextField>
          <TextField label="Web address (URL) of the system" value={form.system_url} onChange={set('system_url')} required placeholder="https://customer.starlimscloud.com/starlims"
            helperText="Only the address is kept; anything after a ? is dropped, and do not include a password." />
          <TextField label="Anything else the team should know (optional)" value={form.note} onChange={set('note')} multiline minRows={2} slotProps={{ htmlInput: { maxLength: 1000 } }} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={send} disabled={busy || !ready}>{busy ? 'Sending...' : 'Send request'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function CustomerLogs() {
  const [customers, setCustomers] = useState(null);
  const [customerId, setCustomerId] = useState('');
  const [environment, setEnvironment] = useState('');
  const [server, setServer] = useState('');
  const [logType, setLogType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [name, setName] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(null);
  const [selected, setSelected] = useState({});        // fileKey -> file
  const [zipping, setZipping] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { customerLogsAPI.customers().then((r) => setCustomers(r.data)).catch((err) => { setError(errText(err, 'Could not load customers')); setCustomers([]); }); }, []);

  const customer = customers?.find((c) => String(c.id) === String(customerId));
  const environments = customer ? [...new Set(customer.sources.map((s) => s.environment))] : [];
  const inEnv = customer ? customer.sources.filter((s) => s.environment === environment) : [];
  const servers = [...new Set(inEnv.map((s) => s.server))];
  const types = [...new Set(inEnv.filter((s) => !server || s.server === server).map((s) => s.log_type))];

  const pickCustomer = (id) => {
    setCustomerId(id); setServer(''); setLogType(''); setResult(null);
    const c = customers.find((x) => String(x.id) === String(id));
    const envs = c ? [...new Set(c.sources.map((s) => s.environment))] : [];
    setEnvironment(envs.length === 1 ? envs[0] : '');
  };
  const pickEnvironment = (v) => { setEnvironment(v); setServer(''); setLogType(''); setResult(null); };

  const params = { customer_id: customerId, environment };
  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await customerLogsAPI.files({ ...params, server: server || undefined, log_type: logType || undefined, date_from: from || undefined, date_to: to || undefined, q: name.trim() || undefined });
      setResult(res.data); setPage(0); setSelected({});
    } catch (err) { setError(errText(err, 'Could not load the logs')); setResult(null); }
    setLoading(false);
  };

  const pageFiles = result ? result.files.slice(page * 50, page * 50 + 50) : [];
  const chosen = Object.values(selected);
  const allOnPage = pageFiles.length > 0 && pageFiles.every((f) => selected[fileKey(f)]);
  const someOnPage = pageFiles.some((f) => selected[fileKey(f)]);
  const toggle = (f) => setSelected((cur) => { const n = { ...cur }; if (n[fileKey(f)]) delete n[fileKey(f)]; else n[fileKey(f)] = f; return n; });
  const togglePage = () => setSelected((cur) => { const n = { ...cur }; pageFiles.forEach((f) => { if (allOnPage) delete n[fileKey(f)]; else n[fileKey(f)] = f; }); return n; });
  const selectAll = () => setSelected(Object.fromEntries(result.files.map((f) => [fileKey(f), f])));
  const downloadSelected = async () => {
    setZipping(true); setError(null);
    try {
      const res = await customerLogsAPI.zip({ customer_id: Number(customerId), environment, files: chosen.map((f) => ({ server: f.server, log_type: f.log_type, name: f.name })) });
      saveBlob(res.data, `${result.customer}-${environment}-logs.zip`);
    } catch (err) {
      // a failed blob download carries its error as a blob, so read the message out of it
      let detail = null;
      try { detail = JSON.parse(await blobText(err.response.data)).detail; } catch (e) { /* not JSON */ }
      setError(detail || errText(err, 'Could not download the selected files'));
    }
    setZipping(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" gutterBottom>Customer Logs</Typography>
        <Button variant="outlined" size="small" startIcon={<ReportProblemOutlinedIcon />} onClick={() => setReporting(true)}>Report missing environment</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Log files copied from the customers' servers every 15 minutes. Choose a customer and an environment, then load the logs. Can't find an environment? Use the button above.
      </Typography>
      {notice && (
        <Alert severity={notice.email_status === 'sent' ? 'success' : 'warning'} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.email_status === 'sent'
            ? `Request #${notice.id} sent to ${notice.sent_to} Cloud Services admins for ${notice.customer} ${notice.environment}. You are copied on the email.`
            : `Request #${notice.id} is saved for ${notice.customer} ${notice.environment}, but the email could not be sent. Please tell the Cloud team directly.`}
          {notice.already_reported && ` The same environment was already reported by ${notice.already_reported.requested_by} on ${new Date(notice.already_reported.requested_at).toLocaleDateString()}.`}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {customers === null ? <CircularProgress /> : (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField select size="small" label="Customer" value={customerId} onChange={(e) => pickCustomer(e.target.value)} slotProps={NATIVE} sx={{ minWidth: 260 }}>
              <option value="">Select a customer</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.cust_code} - {c.cust_name}</option>)}
            </TextField>
            <TextField select size="small" label="Environment" value={environment} onChange={(e) => pickEnvironment(e.target.value)} slotProps={NATIVE} disabled={!customer} sx={{ minWidth: 170 }}>
              <option value="">{customer ? 'Select an environment' : '-'}</option>
              {environments.map((e) => <option key={e} value={e}>{e}</option>)}
            </TextField>
            <TextField select size="small" label="Server" value={server} onChange={(e) => { setServer(e.target.value); setLogType(''); }} slotProps={NATIVE} disabled={!environment} sx={{ minWidth: 180 }}>
              <option value="">All servers</option>{servers.map((s) => <option key={s} value={s}>{s}</option>)}
            </TextField>
            <TextField select size="small" label="Log type" value={logType} onChange={(e) => setLogType(e.target.value)} slotProps={NATIVE} disabled={!environment} sx={{ minWidth: 130 }}>
              <option value="">All types</option>{types.map((t) => <option key={t} value={t}>{t}</option>)}
            </TextField>
            <TextField size="small" type="date" label="From" value={from} onChange={(e) => setFrom(e.target.value)} slotProps={SHRINK} disabled={!environment} />
            <TextField size="small" type="date" label="To" value={to} onChange={(e) => setTo(e.target.value)} slotProps={SHRINK} disabled={!environment} />
            <TextField size="small" label="File name contains" value={name} onChange={(e) => setName(e.target.value)} disabled={!environment} sx={{ minWidth: 170 }} />
            <Button variant="contained" onClick={load} disabled={!environment || loading}>{loading ? 'Loading...' : 'Load logs'}</Button>
          </Box>
          {customers.length === 0 && <Typography color="text.secondary" sx={{ mt: 1 }}>No customer has any log source yet.</Typography>}
        </Paper>
      )}
      {result && (
        <Paper variant="outlined">
          <Box sx={{ p: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {result.files.length === 0 ? 'No log files found. Files appear after the first sync from the server; check the filters and try again.' : `${result.files.length} files for ${result.customer} / ${result.environment}.`}
              {result.truncated && ' Only the first 5,000 are listed; narrow the dates or type.'}
            </Typography>
          </Box>
          {chosen.length > 0 && (
            <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip label={`${chosen.length} selected`} color="primary" />
              <Button variant="contained" size="small" startIcon={<DownloadIcon />} onClick={downloadSelected} disabled={zipping || chosen.length > MAX_ZIP}>
                {zipping ? 'Preparing zip...' : 'Download selected (zip)'}
              </Button>
              {chosen.length < result.files.length && <Button size="small" onClick={selectAll}>Select all {result.files.length} files</Button>}
              <Button size="small" onClick={() => setSelected({})}>Clear selection</Button>
              {chosen.length > MAX_ZIP && <Typography variant="body2" color="error">Select at most {MAX_ZIP} files at a time; narrow the dates or log type.</Typography>}
            </Box>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow><TableCell padding="checkbox"><Checkbox size="small" checked={allOnPage} indeterminate={someOnPage && !allOnPage} onChange={togglePage} slotProps={{ input: { 'aria-label': 'Select all files on this page' } }} /></TableCell><TableCell>Date</TableCell><TableCell>File</TableCell><TableCell>Type</TableCell><TableCell>Server</TableCell><TableCell align="right">Stored</TableCell><TableCell>Synced</TableCell></TableRow></TableHead>
              <TableBody>
                {pageFiles.map((f) => (
                  <TableRow key={fileKey(f)} hover selected={!!selected[fileKey(f)]} onClick={() => setOpen(f)} sx={{ cursor: 'pointer' }}>
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox size="small" checked={!!selected[fileKey(f)]} onChange={() => toggle(f)} slotProps={{ input: { 'aria-label': `Select ${f.name}` } }} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{f.date}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-all' }}>{f.name}</TableCell>
                    <TableCell>{f.log_type}</TableCell><TableCell>{f.server}</TableCell>
                    <TableCell align="right">{fmtSize(f.stored_bytes)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTime(f.synced_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={result.files.length} page={page} rowsPerPage={50} rowsPerPageOptions={[50]} onPageChange={(_, p) => setPage(p)} />
        </Paper>
      )}
      {reporting && <MissingEnvironmentDialog initialCustomerId={customerId} onClose={() => setReporting(false)} onSent={(r) => { setReporting(false); setNotice(r); }} />}
      {open && <Viewer file={open} params={params} onClose={() => setOpen(null)} />}
    </Box>
  );
}

export default CustomerLogs;
